/**
 * Party — the customer or partner identity behind an engagement.
 *
 * Authority: NIMER-SCOPE-DM-001 §4 (`parties`), NIMER-SCOPE-TAX-001 §4
 * (entry-mode data), PRD §4.5.
 */
import { z } from 'zod';
import { EmailSchema, NonEmptyTextSchema, OptionKeySchema, TimestampSchema, UuidSchema, } from './primitives.js';
/** PRD §4.5. Partner mode is a fast-follow slice; the field exists from v1. */
export const ENTRY_MODES = ['customer', 'partner_on_behalf'];
export const EntryModeSchema = z.enum(ENTRY_MODES);
/**
 * Whether commercial pricing is visible to the end customer in partner mode.
 * This is one of three independent suppression axes (scope-0d0) — it is not a
 * synonym for `white_label`, and collapsing them would leak pricing.
 */
export const PRICING_VISIBILITIES = ['partner_only', 'end_customer_visible'];
export const PricingVisibilitySchema = z.enum(PRICING_VISIBILITIES);
/** DM-001 §2.1 `contact` answer shape, and the `parties.contact` jsonb column. */
export const ContactSchema = z.object({
    name: NonEmptyTextSchema,
    email: EmailSchema,
    phone: z.string().max(40).nullable(),
    role: z.string().max(120).nullable(),
});
/**
 * The Who You Are org profile (IMPL-SVCLINES-001 §4) — country, sector and a
 * size band, captured once at entry for every service line. `sector` shares
 * GEN.ORG.04's option-key space verbatim (the enum is the pack's, not forked
 * here); `country` keys match GEN.ORG.03.
 */
export const ORG_SIZE_BANDS = [
    'emp_1_50',
    'emp_51_250',
    'emp_251_1000',
    'emp_1001_5000',
    'emp_5000_plus',
];
export const OrgSizeBandSchema = z.enum(ORG_SIZE_BANDS);
export const ORG_COUNTRIES = ['QA', 'SA', 'AE', 'KW', 'BH', 'OM', 'other'];
export const OrgCountrySchema = z.enum(ORG_COUNTRIES);
export const OrgProfileSchema = z.object({
    country: OrgCountrySchema,
    sector: OptionKeySchema,
    size_band: OrgSizeBandSchema,
});
/**
 * Normalised phone (IMPL-SVCLINES-001 §4.1 #5): spaces, dashes and parens are
 * stripped, then the result must be 7–15 digits with an optional leading `+`.
 * Shared client/server so the form and the API can never disagree on what a
 * valid number is. `ContactSchema.phone` keeps its loose wire shape for
 * back-compat; this is layered on by {@link WhoYouAreSchema}.
 */
export const PhoneSchema = z
    .string()
    .transform((v) => v.replace(/[\s\-()]/g, ''))
    .pipe(z.string().regex(/^\+?[0-9]{7,15}$/));
export const PartySchema = z.object({
    id: UuidSchema,
    entry_mode: EntryModeSchema,
    org_name: NonEmptyTextSchema,
    contact: ContactSchema,
    partner_org: z.string().max(500).nullable(),
    partner_contact: ContactSchema.nullable(),
    partner_ref: z.string().max(200).nullable(),
    end_customer_org: z.string().max(500).nullable(),
    partner_branding: z.boolean(),
    pricing_visibility: PricingVisibilitySchema,
    end_customer_contact_visible: z.boolean(),
    white_label: z.boolean(),
    /** Nullable on reads: rows created before IMPL-SVCLINES-001 have none. */
    org_profile: OrgProfileSchema.nullable(),
    created_at: TimestampSchema,
});
/**
 * Portal-entry payload. The server assigns `id` and `created_at`; a client that
 * sends them is ignored, not trusted.
 */
export const PartyCreateRequestSchema = z.object({
    entry_mode: EntryModeSchema,
    org_name: NonEmptyTextSchema,
    contact: ContactSchema,
    partner_org: z.string().max(500).nullish(),
    partner_contact: ContactSchema.nullish(),
    partner_ref: z.string().max(200).nullish(),
    end_customer_org: z.string().max(500).nullish(),
    partner_branding: z.boolean().default(false),
    pricing_visibility: PricingVisibilitySchema.default('partner_only'),
    end_customer_contact_visible: z.boolean().default(false),
    white_label: z.boolean().default(false),
    /** Required on create (IMPL-SVCLINES-001 §4.3); reads stay nullable. */
    org_profile: OrgProfileSchema,
});
/**
 * The canonical Who You Are validator (IMPL-SVCLINES-001 §4.3) — the create
 * request plus the one rule the base object cannot express without changing
 * `ContactSchema`'s wire shape: the contact phone (and the partner contact's,
 * in partner mode) must be present and {@link PhoneSchema}-valid. Layered via
 * `superRefine` exactly like the web's `entryMode.ts` partner gate, so every
 * existing consumer of `ContactSchema` keeps compiling untouched.
 */
export const WhoYouAreSchema = PartyCreateRequestSchema.superRefine((value, ctx) => {
    requirePhone(ctx, value.contact.phone, ['contact', 'phone']);
    if (value.entry_mode === 'partner_on_behalf' && value.partner_contact != null) {
        requirePhone(ctx, value.partner_contact.phone, ['partner_contact', 'phone']);
    }
});
function requirePhone(ctx, phone, path) {
    if (phone === null || phone.trim() === '') {
        ctx.addIssue({ code: 'custom', path: [...path], message: 'required' });
        return;
    }
    if (!PhoneSchema.safeParse(phone).success) {
        ctx.addIssue({ code: 'custom', path: [...path], message: 'invalid' });
    }
}
//# sourceMappingURL=party.js.map